import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  useGetFieldAdminTeamOverviewQuery,
  useGetFieldAdminQEOversightQuery,
  useGetFieldAdminVehiclesQuery,
} from "./fieldAdminApi";
import { logout, getUserName } from "../Authentication/authSlice";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { useConfirm } from "./FAControls";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { BAD_RED_TEXT, teamLabel, cocFigure } from "./designTokens";
import { FASectionLabel, FAPageShell, FAPageHeader } from "./FALayout";

export default function More() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userName = useSelector(getUserName);
  const { ask, confirmDialog } = useConfirm();

  // The design's rows summarise what is behind them rather than describing it — "Team B-4,
  // 5 installers, 1 QE, vehicle BBD 421 GP" answers the question the row exists to answer,
  // so the menu is worth reading rather than only worth tapping. All three queries are
  // already cached by the pages they belong to.
  const { data: team } = useGetFieldAdminTeamOverviewQuery();
  const { data: qe } = useGetFieldAdminQEOversightQuery();
  const { data: fleet } = useGetFieldAdminVehiclesQuery();

  const van = (fleet?.vehicles || []).find(
    (v) => v.assigned_team_id === fleet?.team_id && v.status !== "retired",
  );

  const teamSub = team
    ? [
        `${team.installer_count} installer${team.installer_count === 1 ? "" : "s"}`,
        team.qe ? "1 QE" : "no QE",
        van ? `vehicle ${van.registration}` : "no vehicle",
      ].join(" · ")
    : "Members, vehicle & area assignment";

  // Issued against everything raised today: the chip answers "how far through is the QE",
  // which is the reason to open the page at all.
  const cocsTotal = qe ? cocFigure(qe).total : null;

  const handleSignOut = async () => {
    // Design copy, minus its "any queued writes are held until the device is back online"
    // sentence — that describes the offline write queue, which is FA-RFI-5 and unbuilt.
    const proceed = await ask({
      title: "Sign out?",
      body: "You will need to sign back in to continue.",
      confirmLabel: "Sign out",
      danger: true,
    });
    if (!proceed) return;
    dispatch(logout());
    dispatch(showSuccess("Signed out"));
  };

  return (
    <>
      <FAPageShell>
        <FAPageHeader title="More" sub="Team management, oversight, logs and settings." sx={{ mb: "24px" }} />

        <FASectionLabel>Team & oversight</FASectionLabel>
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden", mb: "24px" }}>
          <MenuRow
            Icon={GroupsOutlinedIcon} iconBg="rgba(253,185,64,0.12)" iconColor={AMBER}
            name={team?.team_name ? `Team — ${teamLabel(team.team_name, team.team_id)}` : "Team"}
            sub={teamSub}
            chip={team ? String(team.installer_count) : null}
            onClick={() => navigate("/field-admin/more/team/")}
          />
          <MenuRow
            Icon={FactCheckOutlinedIcon} iconBg="rgba(168,85,247,0.12)" iconColor="#C4B5FD"
            name="QE oversight" sub="COC completion progress · read-only"
            chip={cocsTotal ? `${qe.cocs_issued_today}/${cocsTotal}` : null}
            onClick={() => navigate("/field-admin/more/qe-oversight/")}
          />
          <MenuRow Icon={LocalShippingOutlinedIcon} iconBg="rgba(253,185,64,0.12)" iconColor={AMBER} name="Fleet" sub="Vehicle register · add, assign, retire" onClick={() => navigate("/field-admin/more/fleet/")} />
          <MenuRow Icon={PhoneAndroidOutlinedIcon} iconBg="rgba(59,130,246,0.12)" iconColor="#93C5FD" name="Devices" sub="Phone & tablet register · issue, decommission" onClick={() => navigate("/field-admin/more/devices/")} isLast />
        </Box>

        <FASectionLabel>Records & settings</FASectionLabel>
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden", mb: "24px" }}>
          <MenuRow Icon={DescriptionOutlinedIcon} iconBg="rgba(34,197,94,0.12)" iconColor="#4ADE80" name="H&S records" sub="Date-grouped history · DoL-ready PDF" onClick={() => navigate("/field-admin/hs/history/")} />
          <MenuRow Icon={PersonOutlineIcon} iconBg="rgba(255,255,255,0.05)" iconColor="rgba(255,255,255,0.6)" name="Profile & settings" sub={[userName, team?.company_name].filter(Boolean).join(" · ") || "—"} onClick={() => navigate("/field-admin/more/profile/")} isLast />
        </Box>

        <Box sx={{ backgroundColor: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.18)", borderRadius: "10px", py: "13px", textAlign: "center", cursor: "pointer" }} onClick={handleSignOut}>
          <Typography sx={{ display: "inline-flex", alignItems: "center", gap: "8px", color: BAD_RED_TEXT, fontSize: "0.88rem", fontWeight: 600 }}>
            <LogoutIcon sx={{ fontSize: "1.05rem" }} /> Sign out
          </Typography>
        </Box>
      </FAPageShell>

      {confirmDialog}
    </>
  );
}

function MenuRow({ Icon, iconBg, iconColor, name, sub, chip, isLast, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        px: "18px",
        py: "14px",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)",
        cursor: "pointer",
      }}
    >
      <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: "1.15rem" }} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>{name}</Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.74rem", mt: "2px" }}>{sub}</Typography>
      </Box>
      {/* .menu-meta .chip — the count sits before the chevron, so the row's headline number
          is readable without opening it. */}
      {chip ? (
        <Box component="span" sx={{
          flexShrink: 0, fontSize: "0.66rem", fontWeight: 700, px: "8px", py: "2px",
          borderRadius: "4px", letterSpacing: "0.05em", textTransform: "uppercase",
          backgroundColor: "rgba(253,185,64,0.14)", color: AMBER,
        }}>
          {chip}
        </Box>
      ) : null}
      <ArrowForwardIosIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem", flexShrink: 0 }} />
    </Box>
  );
}
